import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import MarkdownText from '../../components/foundations/MarkdownText';
import css from './EphemeralUpdatesPanel.css';

const DEFAULT_TITLE = 'Updates';
const LETTER_DELAY_S = 0.06;

// Single right-to-left scan: prefer the most recent streaming-with-title entry,
// otherwise the most recent entry with any title. Avoids allocating two reversed
// copies of the updates array on every render.
const pickHeaderTitle = updates => {
    let fallback = null;
    for (let i = updates.length - 1; i >= 0; i--) {
        const u = updates[i];
        if (!u.title) continue;
        if (u.isStreaming) return u.title;
        if (!fallback) fallback = u.title;
    }
    return fallback || DEFAULT_TITLE;
};

const ShimmerTitle = ({ text }) => (
    <span className={css.shimmerTitle} aria-label={text}>
        {Array.from(text).map((char, idx) => (
            <span
                key={`${idx}-${char}`}
                className={css.shimmerLetter}
                style={{ animationDelay: `${idx * LETTER_DELAY_S}s` }}
                aria-hidden="true"
            >
                {char === ' ' ? ' ' : char}
            </span>
        ))}
    </span>
);

ShimmerTitle.propTypes = { text: PropTypes.string.isRequired };

const EphemeralUpdatesPanel = ({ updates }) => {
    const isStreaming = updates.some(({ isStreaming: streaming }) => streaming);
    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        if (!isStreaming) setIsOpen(false);
    }, [isStreaming]);

    const headerTitle = useMemo(() => pickHeaderTitle(updates), [updates]);

    if (!updates.length) return null;

    return (
        <div className={css.panel}>
            <button
                type="button"
                className={css.header}
                onClick={() => setIsOpen(prevIsOpen => !prevIsOpen)}
                aria-expanded={isOpen}
            >
                {isStreaming && (
                    <>
                        <Icon name="loader" size={IconSizes.X_SMALL} className={css.headerSpinner} />
                        <ShimmerTitle text={headerTitle} />
                    </>
                )}
                {!isStreaming && (
                    <span className={css.headerLabel}>{headerTitle}</span>
                )}
                <span className={classNames(css.chevron, { [css.chevronOpen]: isOpen })}>▸</span>
            </button>
            {isOpen && (
                <ul className={css.list}>
                    {updates.map(update => (
                        <li
                            key={update.id}
                            className={classNames(css.entry, { [css.entryStreaming]: update.isStreaming })}
                        >
                            <MarkdownText content={update.text || ''} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

EphemeralUpdatesPanel.propTypes = {
    updates: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string,
            text: PropTypes.string,
            isStreaming: PropTypes.bool,
        })
    ).isRequired,
};

export default EphemeralUpdatesPanel;
